// src/components/DeployedVehiclesGraph.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  LabelList,
  Legend,
  CartesianGrid,
} from "recharts";

const COLORS = ["#7dd3fc", "#86efac", "#fca5a5"]; // BYD, MG, Citroen
const variants = ["BYD", "MG", "Citroen"];

export default function DeployedVehiclesGraph({ misData, autoDelay = 15000 }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef(null);

  // Prepare city data
  const cityKeys = ["hyd_ohm", "bgl_ohm", "mgl_ohm"];
  const cityNames = ["Hyderabad", "Bangalore", "Mangalore"];

  const allCityData = cityKeys.map((key, i) => ({
    name: cityNames[i],
    BYD: misData?.info?.[key]?.deployed_vehicles?.byd || 0,
    MG: misData?.info?.[key]?.deployed_vehicles?.mg || 0,
    Citroen: misData?.info?.[key]?.deployed_vehicles?.citroen || 0,
  }));

  const cityCount = allCityData.length;

  // Auto rotate city
  useEffect(() => {
    if (cityCount === 0) return;

    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % cityCount);
    }, autoDelay);

    return () => clearInterval(intervalRef.current);
  }, [cityCount, autoDelay]);

  // If no data, render nothing
  if (!misData?.info || cityCount === 0) return null;

  const city = allCityData[currentIndex];

  // Label renderer
  const renderLabel = (props, variant) => {
    const { x, y, width, value } = props;
    return (
      <text
        x={x + width / 2}
        y={y - 5}
        fill="#111"
        fontSize={12}
        fontWeight="bold"
        textAnchor="middle"
      >
        {`${variant}: ${value}`}
      </text>
    );
  };

  const handleDotClick = (idx) => {
    setCurrentIndex(idx);
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % cityCount);
    }, autoDelay);
  };

  return (
 <div className="flex flex-col items-center w-full h-full">
  <div className="bg-white shadow-md w-full h-full flex flex-col">
    <h2 className="text-lg font-bold text-gray-800 text-center py-2">
      {city.name} Deployed Vehicles
    </h2>
    <div className="flex-1">
      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={[city]}
          margin={{ top: 20, right: 20, left: 10, bottom: 10 }}
          barGap={15}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" tick={false} axisLine={false} />
          <YAxis />
          <Legend />
          {variants.map((variant, idx) => (
            <Bar
              key={variant}
              dataKey={variant}
              fill={COLORS[idx]}
              barSize={60}
            >
              <LabelList
                dataKey={variant}
                content={(props) => renderLabel(props, variant)}
              />
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  </div>

  {/* Carousel dots */}
  <div className="flex mt-2 space-x-2">
    {allCityData.map((_, idx) => (
      <button
        key={idx}
        onClick={() => handleDotClick(idx)}
        className={`w-3 h-3 rounded-full transition-all ${
          idx === currentIndex ? "bg-blue-600 w-4 h-4" : "bg-gray-400"
        }`}
      />
    ))}
  </div>
 </div>
  );
}

